var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { CircularProgress, Typography, useTheme } from '@mui/material';
import { useDinPlayerContext } from 'hx-din-test-2';
import { useNavigator } from 'hx-navigator';
import { Fade, Keypad, SideImageLayout, TripletInput, useKeypad, useSimpleModal, } from 'hx-ui';
import { dinTestActions, selectActiveTestStep, updateEarlySubmitWarning, } from '@/store';
import { Footer } from '@/components/footer.component';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useMount } from 'react-use';
import { DinTestSidePanel } from '@/components/din-test-side-panel.component';
import { StyledProgress } from '@/components/styled-progress.component';
import { useSetProgress } from 'hx-progress';
import { useIdleTimer } from 'react-idle-timer';
import { ErrorMessage } from '@/components/error-message.component';
import { NavigationButton, NavigationButtonsContainer, } from '@/components/navigation-button.component';
export function DinTestPage() {
    var theme = useTheme();
    var id = useParams().id;
    var dispatch = useDispatch();
    var _a = useDinPlayerContext(), playTriplet = _a.playTriplet, initialize = _a.initialize, state = _a.state, stop = _a.stop;
    var _b = useKeypad(), value = _b.value, handleChange = _b.handleChange, handleBackspace = _b.handleBackspace, isShaking = _b.isShaking, shake = _b.shake, setValue = _b.setValue;
    var next = useNavigator({
        phaseLength: 23,
        onComplete: function () {
            dispatch(dinTestActions.processResult());
        },
    }).next;
    var activeStep = useSelector(selectActiveTestStep((id && +id - 1) || 0));
    var earlySubmitWarningState = useSelector(function (state) { return state.earlySubmitWarning; });
    var _c = useState(''), idleText = _c[0], setIdleText = _c[1];
    useMount(function () {
        if (!state.initialized)
            initialize();
        dispatch(dinTestActions.initializeTest());
    });
    useEffect(function () {
        dispatch(dinTestActions.setSnr(id));
    }, [id]);
    // Modals
    var _d = useSimpleModal(_jsxs("div", __assign({ className: "grid place-items-center gap-6" }, { children: [_jsx(Typography, __assign({ variant: "h5" }, { children: "Well done!" })), _jsx(Typography, __assign({ variant: "body1" }, { children: "You are halfway through the test, don't give up now." }))] })), {
        toggleWhen: id === '12',
        modalProps: {
            sx: {
                gap: '8px',
                padding: '40px',
            },
        },
    }), halfwayModal = _d[0], isHalfwayModalOpen = _d[2];
    var _e = useSimpleModal(_jsx("div", __assign({ className: "grid place-items-center" }, { children: _jsx(Typography, __assign({ variant: "body1", className: "p-0" }, { children: "Please enter the numbers you heard." })) })), {
        modalProps: {
            sx: {
                gap: '8px',
                padding: '40px',
            },
        },
    }), helpModal = _e[0], toggleHelpModal = _e[1], isHelpModalOpen = _e[2];
    var _f = useSimpleModal(_jsxs("div", __assign({ className: "grid place-items-center gap-4" }, { children: [_jsx(Typography, __assign({ variant: "h5" }, { children: "Warning" })), _jsx(Typography, __assign({ variant: "body1" }, { children: "We noticed your responses are being submitted before the voice prompt has been completed, this will have an adverse affect on your results and we recommend you take your time to complete the test." }))] })), {
        toggleWhen: earlySubmitWarningState['early-submit-count'] >= 3 &&
            !earlySubmitWarningState['warning-given'],
        modalProps: {
            sx: {
                gap: '8px',
                padding: '40px',
            },
        },
    }), earlySubmitModal = _f[0], isEarlySubmitModalOpen = _f[2];
    var playChallenge = useCallback(function () {
        if ((activeStep === null || activeStep === void 0 ? void 0 : activeStep.snr) === undefined)
            return;
        playTriplet(activeStep.challenge, {
            snr: activeStep.snr,
        });
    }, [activeStep]);
    useEffect(function () {
        if (isHalfwayModalOpen || isEarlySubmitModalOpen)
            return;
        playChallenge();
    }, [activeStep, isHalfwayModalOpen, isEarlySubmitModalOpen]);
    var increment = function () {
        dispatch(updateEarlySubmitWarning({
            'early-submit-count': earlySubmitWarningState['early-submit-count'] + 1,
        }));
    };
    var setModalTriggeredTrue = function () {
        dispatch(updateEarlySubmitWarning({
            'warning-given': true,
        }));
    };
    var handleSubmit = useCallback(function () {
        if (value.length < 3)
            return shake();
        var response = value;
        if (state.status !== 'ready') {
            increment();
        }
        dispatch(dinTestActions.submitResponse({ response: response, id: id }));
        setValue([]);
        stop();
        next();
    }, [value, id]);
    useEffect(function () {
        if (isEarlySubmitModalOpen) {
            setModalTriggeredTrue();
        }
    }, [isEarlySubmitModalOpen]);
    useSetProgress(1, (id && +id) || 0);
    var reset = useIdleTimer({
        onIdle: function () {
            if (isHalfwayModalOpen || isHelpModalOpen || isEarlySubmitModalOpen) {
                return;
            }
            setIdleText("If you are unsure, it's okay to guess");
        },
        onActive: function () {
            setIdleText('');
            reset();
        },
        timeout: 1000 * 15,
        events: ['mousedown', 'keypress', 'click'],
    }).reset;
    var keys = [
        '1',
        '2',
        '3',
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
        'Backspace',
        '0',
        'Enter',
    ];
    if (!state.initialized)
        return (_jsx("div", __assign({ className: "grid place-items-center min-h-[31.25rem]" }, { children: _jsx(CircularProgress, {}) })));
    return (_jsx(SideImageLayout, { leftPanel: _jsx(DinTestSidePanel, {}), leftPanelWidth: "40%", header: _jsx(StyledProgress, { className: "px-4 md:px-12 py-3", textClassName: 'font-bold' }), content: _jsxs(_Fragment, { children: [_jsx(Fade, __assign({ className: "grid gap-3 px-4 py-2 place-items-center" }, { children: state.error ? (_jsx(ErrorMessage, { action: playChallenge, actionText: "Retry", message: "Oops! An error occurred while trying to play the audio" })) : (_jsxs(_Fragment, { children: [_jsxs(Typography, __assign({ className: "font-bold text-3xl text-dark-text pb-4" }, { children: ["Step ", id, " of 23"] })), _jsx(TripletInput, { className: "gap-8 flex w-full justify-center", value: value, isShaking: isShaking, digitStyle: {
                                    fontSize: '1.875rem',
                                    minHeight: '2em',
                                    borderBottomWidth: '2px',
                                    borderBottomColor: theme.palette.primary.main,
                                    borderBottomStyle: 'solid !important',
                                    width: '50px',
                                    '&.has-value': {
                                        borderBottomColor: theme.palette.secondary.main,
                                    },
                                } }), _jsx(Keypad, { value: value, onChange: handleChange, onBackspace: handleBackspace, onSubmit: handleSubmit, buttonSize: "large", keys: keys }), helpModal, halfwayModal, earlySubmitModal] })) })), !!idleText && _jsx(Typography, { children: idleText })] }), nav: _jsx(NavigationButtonsContainer, { children: _jsx(NavigationButton, { variant: "outlined", label: "Help", onClick: toggleHelpModal }) }), footer: _jsx(Footer, {}) }));
}
export default DinTestPage;
